import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { TrendyolOrder, TrendyolCredentials } from '../types';
import {
  fetchOrders,
  acceptOrder,
  markReady,
  markShipped,
  markDelivered,
  cancelOrder,
  updateStoreStatus,
  saveOrderToSql,
  updateStatusInSql,
  getTodayOrdersFromSql,
  fetchProductDescriptions,
  TrendyolConflictError,
  parseStatusFromConflictBody,
  type TrendyolPackageStatus,
} from '../services/TrendyolYemekService';

/**
 * Trendyol durum güncelleme sonucu (discriminated union).
 *
 * - `success: true` → istek başarılı, state güncellendi.
 * - `statusMismatch: true` → 409 Conflict alındı, self-heal yapıldı; sipariş Trendyol'da
 *   `currentStatus` durumunda. Yerel state ve SQL bu duruma senkronlandı.
 * - `statusMismatch: false` → 409 dışı bir hata; UI hata snackbar'ı göstermeli.
 */
export type TrendyolUpdateResult =
  | { success: true }
  | { success: false; statusMismatch: true; currentStatus: string; attemptedAction: 'accept' | 'ready' | 'shipped' | 'delivered' }
  | { success: false; statusMismatch: false; message: string };

const POLLING_INTERVAL_MS = 25000; // 25 saniye
const ERROR_BACKOFF_MAX_MS = 120000; // Hata durumunda max 2 dakika

// local-service bu versiyondan itibaren server-side polling'i destekler.
// Eski sürümlerde browser-based polling fallback'i kullanılır (regresyon yok).
const SERVER_SIDE_POLLING_MIN_VERSION = '1.4.3';

// "1.4.3" gibi semver string'leri karşılaştırır. Sadece major.minor.patch.
// a > b → 1, a < b → -1, eşit → 0. Eksik segmentler 0 sayılır.
function compareVersions(a: string, b: string): number {
  const pa = (a || '0.0.0').split('.').map(p => parseInt(p, 10) || 0);
  const pb = (b || '0.0.0').split('.').map(p => parseInt(p, 10) || 0);
  for (let i = 0; i < 3; i++) {
    const av = pa[i] || 0;
    const bv = pb[i] || 0;
    if (av > bv) return 1;
    if (av < bv) return -1;
  }
  return 0;
}

interface PlatformIntegration {
  id: number;
  platform: string;
  isEnabled: boolean;
  branchId?: string | null;
  sellerId?: string | null;
  restaurantId?: string | null;
  apiKey?: string | null;
  apiSecret?: string | null;
}

export interface UseTrendyolYemekOptions {
  companyId: string | null;
  branchId?: string | null;
  enabled: boolean;
  platformIntegrations: PlatformIntegration[];
  userEmail: string;
  localServiceUrl?: string | null;
  onNewOrder?: (order: TrendyolOrder) => void;
  onOrderCancelled?: (order: TrendyolOrder) => void;
}

export interface UseTrendyolYemekReturn {
  trendyolOrders: TrendyolOrder[];
  setTrendyolOrders: React.Dispatch<React.SetStateAction<TrendyolOrder[]>>;
  isPolling: boolean;
  vendorStatus: 'OPEN' | 'CLOSED' | 'unknown';
  error: string | null;
  lastPollAt: Date | null;
  localServiceConnected: boolean;
  /** local-service tarafından dönen "version" alanı (örn. "1.4.3"). Yoksa null. */
  localServiceVersion: string | null;
  /**
   * `localServiceVersion >= 1.4.3` ise true. Bu mod aktif olduğunda browser polling
   * durdurulur, local-service kendi setInterval'ı ile Trendyol'u sorgular ve SignalR
   * "TrendyolOrderUpdated" event'i üzerinden push yapar.
   */
  supportsServerSidePolling: boolean;
  /**
   * Server-side polling modunda local-service erişilebilir olduğu halde polling
   * tick'inin uzun süredir dönmediğini gösterir (yeni sipariş DÜŞMÜYOR demektir).
   * Çözüm: GTS masaüstü yazılımını yeniden başlatmak. Eski local-service
   * sürümlerinde bu bilgi olmadığından her zaman false kalır.
   */
  serverPollingStale: boolean;
  updateOrderStatus: (packageId: string, action: 'accept' | 'ready' | 'shipped' | 'delivered', preparationTime?: number) => Promise<TrendyolUpdateResult>;
  cancelTrendyolOrder: (packageId: string, itemIdList: string[], reasonId: number) => Promise<boolean>;
  openVendor: () => Promise<boolean>;
  closeVendor: () => Promise<boolean>;
  refreshOrders: () => Promise<void>;
  trendyolProductDescriptions: Map<number, string>;
}

export function useTrendyolYemek(options: UseTrendyolYemekOptions): UseTrendyolYemekReturn {
  const { companyId, branchId, enabled, platformIntegrations, userEmail, localServiceUrl, onNewOrder, onOrderCancelled } = options;

  const [trendyolOrders, setTrendyolOrders] = useState<TrendyolOrder[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [vendorStatus, setVendorStatus] = useState<'OPEN' | 'CLOSED' | 'unknown'>('unknown');
  const [error, setError] = useState<string | null>(null);
  const [lastPollAt, setLastPollAt] = useState<Date | null>(null);
  const [localServiceConnected, setLocalServiceConnected] = useState(true);
  const [localServiceVersion, setLocalServiceVersion] = useState<string | null>(null);
  // Race-condition guard'ı: /api/status yanıtı dönene kadar (başarılı veya başarısız)
  // legacy polling'i de başlatmıyoruz. Aksi takdirde browser, version'ı öğrenmeden
  // 1-2 fetch yapıp local-service polling'i ile çakışıyor.
  const [versionCheckCompleted, setVersionCheckCompleted] = useState(false);
  // Local-service ayakta ama polling tick'i dönmüyor (örn. 2026-08-03 deadlock'u).
  const [serverPollingStale, setServerPollingStale] = useState(false);
  const [trendyolProductDescriptions, setTrendyolProductDescriptions] = useState<Map<number, string>>(new Map());
  const productDescFetchedRef = useRef(false);

  // PR3.0: Server-side polling sadece local-service v1.4.3+ ve canlı bağlantı varsa
  // aktif. Bunun dışındaki tüm durumlarda eski browser polling AYNEN devam eder
  // (legacy mode = sıfır regresyon).
  const supportsServerSidePolling = useMemo(() => {
    if (!localServiceConnected) return false;
    if (!localServiceVersion) return false;
    return compareVersions(localServiceVersion, SERVER_SIDE_POLLING_MIN_VERSION) >= 0;
  }, [localServiceConnected, localServiceVersion]);

  const previousOrderIdsRef = useRef<Set<string>>(
    (() => {
      try {
        const stored = localStorage.getItem('trendyol_known_order_ids');
        if (stored) return new Set<string>(JSON.parse(stored));
      } catch { /* ignore */ }
      return new Set<string>();
    })()
  );
  const errorCountRef = useRef(0);
  const isFirstPollRef = useRef(true);
  const onNewOrderRef = useRef(onNewOrder);
  onNewOrderRef.current = onNewOrder;
  const onOrderCancelledRef = useRef(onOrderCancelled);
  onOrderCancelledRef.current = onOrderCancelled;
  const previousStatusMapRef = useRef<Map<string, string>>(new Map());

  const getCredentials = useCallback((): TrendyolCredentials | null => {
    const integration = platformIntegrations.find(
      p => p.platform === 'trendyol_yemek' && p.isEnabled &&
        (!branchId || p.branchId === branchId)
    );
    if (!integration) {
      console.warn('[TrendyolYemek] ❌ Entegrasyon bulunamadı:', {
        platformCount: platformIntegrations.length,
        platforms: platformIntegrations.map(p => `${p.platform}(enabled=${p.isEnabled},branch=${p.branchId})`),
        arananBranchId: branchId,
      });
      return null;
    }

    const supplierId = integration.sellerId;
    const storeId = integration.restaurantId;
    const apiKey = integration.apiKey;
    const apiSecret = integration.apiSecret;

    if (!supplierId || !storeId || !apiKey || !apiSecret || !userEmail) {
      console.warn('[TrendyolYemek] ❌ Credential eksik:', {
        supplierId: !!supplierId,
        storeId: !!storeId,
        apiKey: !!apiKey,
        apiSecret: !!apiSecret,
        userEmail: !!userEmail,
      });
      return null;
    }

    return { supplierId, storeId, apiKey, apiSecret, userEmail };
  }, [platformIntegrations, branchId, userEmail]);

  const proxyUrl = localServiceUrl || 'http://localhost:3003';

  const pollOrders = useCallback(async () => {
    const credentials = getCredentials();
    if (!credentials) return;

    const myStoreId = credentials.storeId ? Number(credentials.storeId) : null;
    const statusRank: Record<string, number> = {
      'Created': 1, 'Picking': 2, 'Invoiced': 3, 'Shipped': 4, 'Delivered': 5,
      'Unsupplied': 0, 'Cancelled': 0, 'Returned': 0,
    };

    // ---- ADIM 1: SQL'den bugunku siparisleri cek (kaybolmayan temel) ----
    let sqlOrders: TrendyolOrder[] = [];
    if (companyId) {
      try {
        const allSqlOrders = await getTodayOrdersFromSql(branchId, 1, 500, companyId);
        console.log(`[TrendyolYemek] 📦 SQL'den toplam ${allSqlOrders.length} sipariş geldi (filtre öncesi)`, allSqlOrders.length > 0 ? { storeIds: Array.from(new Set(allSqlOrders.map(o => `${o.storeId}(${typeof o.storeId})`))), myStoreId, myStoreIdType: typeof myStoreId } : {});
        if (myStoreId) {
          sqlOrders = allSqlOrders.filter(o => Number(o.storeId) === myStoreId);
        } else {
          sqlOrders = allSqlOrders;
        }
        console.log(`[TrendyolYemek] 📦 SQL'den ${sqlOrders.length} sipariş yüklendi (filtre sonrası)`);
      } catch (err) {
        console.warn('[TrendyolYemek] SQL fetch hatası (API fallback kullanılacak):', err);
      }
    }

    // ---- ADIM 2: Trendyol API'den guncel veriyi dene ----
    // Not: API-side filtreleme artık URL'ye eklenen `storeId` query parametresi
    // ile yapılıyor (bkz. TrendyolYemekService.fetchOrders). Hook içinde
    // client-side storeId filtresine gerek yok.
    let apiOrders: TrendyolOrder[] = [];
    let apiSuccess = false;
    try {
      // Delivered (teslim edildi) HARİÇ tüm durumlar çekilir.
      // Teslim edilen siparişler zaten SQL'de kayıtlı olduğu için Trendyol'dan
      // tekrar çekilmesine gerek yok; polling yükünün büyük kısmını oluşturuyordu.
      // Cancelled ve Unsupplied dahil edilir (iptal kaydı canlı görünsün diye).
      const statuses: TrendyolPackageStatus[] = ['Created', 'Picking', 'Invoiced', 'Shipped', 'Unsupplied', 'Cancelled'];
      console.log(`[TrendyolYemek] 📡 Sipariş sorgulanıyor (store=${credentials.storeId}, statuses=${statuses.join(',')})...`, proxyUrl);
      const rawOrders = await fetchOrders(credentials, statuses, proxyUrl);

      const seenOrderIds = new Map<string, TrendyolOrder>();
      for (const o of rawOrders) {
        const key = o.orderCode || o.orderId || o.id;
        const existing = seenOrderIds.get(key);
        if (!existing || (o.packageModificationDate || 0) > (existing.packageModificationDate || 0)) {
          seenOrderIds.set(key, o);
        }
      }
      apiOrders = Array.from(seenOrderIds.values());
      apiSuccess = true;
      errorCountRef.current = 0;
      setLocalServiceConnected(true);
      setVendorStatus('OPEN');
      console.log(`[TrendyolYemek] ✅ API polling: ${apiOrders.length} sipariş (ham: ${rawOrders.length}, store=${credentials.storeId})`);

      // Ürün açıklamalarını ilk başarılı polling'de çek (1 saat cache)
      if (!productDescFetchedRef.current) {
        productDescFetchedRef.current = true;
        fetchProductDescriptions(credentials, proxyUrl).then(map => {
          if (map.size > 0) setTrendyolProductDescriptions(map);
        }).catch(() => {});
      }
    } catch (err) {
      errorCountRef.current++;
      const errMsg = err instanceof Error ? err.message : 'Bilinmeyen hata';
      console.warn('[TrendyolYemek] ⚠️ API polling başarısız (SQL verisi kullanılacak):', errMsg);

      const isNetworkError = err instanceof TypeError || errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError');
      if (isNetworkError || errorCountRef.current >= 3) {
        // Double-check via /api/status before marking disconnected
        try {
          const statusResp = await fetch(`${proxyUrl}/api/status`, { signal: AbortSignal.timeout(3000) });
          if (!statusResp.ok) setLocalServiceConnected(false);
        } catch {
          setLocalServiceConnected(false);
        }
      }

      if (sqlOrders.length === 0) {
        setError(errMsg);
        console.error('[TrendyolYemek] Hem SQL hem API başarısız');
        return;
      }
    }

    setLastPollAt(new Date());
    if (apiSuccess) {
      setError(null);
    }

    // ---- ADIM 3: API siparislerini SQL'e kaydet + odeme bilgilerini topla ----
    const paidFromSql = new Set<string>();
    const sqlStatusFromSave = new Map<string, string>();

    for (const so of sqlOrders) {
      if (so.gtsPaid) {
        paidFromSql.add(so.id);
      }
    }

    if (companyId && apiOrders.length > 0) {
      const sqlResults = await Promise.allSettled(
        apiOrders.map(order => saveOrderToSql(order, branchId))
      );
      sqlResults.forEach((res, i) => {
        if (res.status === 'fulfilled' && res.value) {
          if (res.value.isPaid && res.value.invoiceId) {
            paidFromSql.add(apiOrders[i].id);
          }
          if (res.value.sqlStatus) {
            sqlStatusFromSave.set(apiOrders[i].id, res.value.sqlStatus);
          }
        }
      });
      console.log('[TrendyolYemek] 💰 SQL ödeme durumu:', { paidCount: paidFromSql.size });
    }

    // ---- ADIM 4: MERGE — SQL temeli + API guncellemeleri ----
    const mergedMap = new Map<string, TrendyolOrder>();

    // 4a. SQL siparislerini temel olarak ekle (bunlar ASLA kaybolmaz)
    for (const sqlOrder of sqlOrders) {
      mergedMap.set(sqlOrder.id, {
        ...sqlOrder,
        gtsPaid: paidFromSql.has(sqlOrder.id),
      });
    }

    // 4b. API siparisleri ile guncelle (daha taze veri)
    for (const apiOrder of apiOrders) {
      const existing = mergedMap.get(apiOrder.id);
      if (existing) {
        const apiRank = statusRank[apiOrder.packageStatus || ''] || 0;
        const existingRank = statusRank[existing.packageStatus || ''] || 0;
        const savedSqlStatus = sqlStatusFromSave.get(apiOrder.id);
        const savedSqlRank = statusRank[savedSqlStatus || ''] || 0;

        let finalStatus = apiOrder.packageStatus;
        if (existingRank > apiRank && existingRank >= 5) {
          finalStatus = existing.packageStatus;
        }
        if (savedSqlStatus && savedSqlRank > (statusRank[finalStatus || ''] || 0)) {
          finalStatus = savedSqlStatus;
        }

        mergedMap.set(apiOrder.id, {
          ...apiOrder,
          packageStatus: finalStatus,
          gtsPaid: paidFromSql.has(apiOrder.id),
        });
      } else {
        mergedMap.set(apiOrder.id, {
          ...apiOrder,
          gtsPaid: paidFromSql.has(apiOrder.id),
        });
      }
    }

    const mergedOrders = Array.from(mergedMap.values());

    // ---- ADIM 5: State guncelle (prev ile optimistic update korumasi) ----
    setTrendyolOrders(prev => {
      const prevMap = new Map(prev.map(o => [o.id, o]));
      return mergedOrders.map(o => {
        const prevOrder = prevMap.get(o.id);
        if (prevOrder) {
          const prevRank = statusRank[prevOrder.packageStatus || ''] || 0;
          const mergedRank = statusRank[o.packageStatus || ''] || 0;
          if (prevRank > mergedRank && prevRank >= 5) {
            return { ...o, packageStatus: prevOrder.packageStatus };
          }
        }
        return o;
      });
    });

    // ---- ADIM 6: Yeni siparis ve iptal bildirimi ----
    const currentIds = new Set(mergedOrders.map(o => o.id));
    const previousIds = previousOrderIdsRef.current;
    const cancelledStatuses = new Set(['Cancelled', 'UnSupplied', 'Unsupplied', 'Returned']);

    if (isFirstPollRef.current) {
      isFirstPollRef.current = false;
    } else {
      for (const order of mergedOrders) {
        if (order.packageStatus === 'Created' && !previousIds.has(order.id)) {
          onNewOrderRef.current?.(order);
        }

        const prevStatus = previousStatusMapRef.current.get(order.id);
        if (
          prevStatus &&
          !cancelledStatuses.has(prevStatus) &&
          cancelledStatuses.has(order.packageStatus || '')
        ) {
          onOrderCancelledRef.current?.(order);
        }
      }
    }

    previousStatusMapRef.current = new Map(mergedOrders.map(o => [o.id, o.packageStatus || '']));
    previousOrderIdsRef.current = currentIds;
    try {
      localStorage.setItem('trendyol_known_order_ids', JSON.stringify(Array.from(currentIds)));
    } catch { /* ignore */ }
  }, [getCredentials, proxyUrl, companyId, branchId]);

  useEffect(() => {
    console.log('[TrendyolYemek] useEffect kontrol:', { enabled, companyId: !!companyId, hasCredentials: !!getCredentials(), supportsServerSidePolling, localServiceVersion, versionCheckCompleted });
    if (!enabled || !companyId || !getCredentials()) {
      setIsPolling(false);
      return;
    }

    // Race-condition guard: /api/status henüz dönmediyse polling'i geciktir.
    // Aksi takdirde localServiceVersion=null → supportsServerSidePolling=false →
    // legacy polling başlar, sonra version öğrenilince durur ama bu arada 1-2 tick
    // yapılır ve local-service polling'iyle çakışır.
    if (!versionCheckCompleted) {
      console.log('[TrendyolYemek] ⏳ Version check bekleniyor, polling henüz başlamadı');
      setIsPolling(false);
      return;
    }

    // PR3.1: local-service v1.4.3+ ise polling local-service tarafında yapılıyor;
    // browser tarafında setInterval kurmayız. pollOrders fonksiyonu yine de mevcut
    // (refreshOrders manuel çağrısı için). Periodic safety net ayrı bir useEffect'te.
    if (supportsServerSidePolling) {
      console.log(`[TrendyolYemek] 🚀 Server-side polling aktif (local-service v${localServiceVersion}) - browser polling devre dışı`);
      setIsPolling(false);
      // Yine de mount'ta bir kez SQL'den state'i tohumla (sayfa yenilemesi sonrası ilk render).
      (async () => {
        try {
          const credentials = getCredentials();
          if (!credentials) return;
          const myStoreId = credentials.storeId ? Number(credentials.storeId) : null;
          const all = await getTodayOrdersFromSql(branchId, 1, 500, companyId);
          const filtered = myStoreId ? all.filter(o => Number(o.storeId) === myStoreId) : all;
          setTrendyolOrders(filtered);
          console.log(`[TrendyolYemek] 🌱 Initial SQL seed (server-side mode): ${filtered.length} sipariş`);
        } catch (err) {
          console.warn('[TrendyolYemek] Initial SQL seed hatası:', err);
        }
      })();
      return;
    }

    console.log('[TrendyolYemek] ✅ Polling başlatılıyor (legacy mode), proxyUrl:', proxyUrl);
    setIsPolling(true);
    isFirstPollRef.current = true;

    pollOrders();

    const getInterval = () => {
      if (errorCountRef.current === 0) return POLLING_INTERVAL_MS;
      return Math.min(POLLING_INTERVAL_MS * Math.pow(2, errorCountRef.current), ERROR_BACKOFF_MAX_MS);
    };

    let timeoutId: NodeJS.Timeout;
    const scheduleNext = () => {
      timeoutId = setTimeout(async () => {
        if (document.visibilityState === 'visible') {
          await pollOrders();
        }
        scheduleNext();
      }, getInterval());
    };
    scheduleNext();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pollOrders();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setIsPolling(false);
    };
  }, [enabled, companyId, getCredentials, pollOrders, supportsServerSidePolling, localServiceVersion, branchId, versionCheckCompleted]);

  // Health check: ping local-service /api/status on mount + periodically.
  // PR3.0: yanıttaki `version` alanını da okuruz; v1.4.3+ ise server-side polling moduna geçeriz.
  // Eski sürüm yanıtlarında `version` alanı yok → null kalır → legacy mode korunur (regresyon yok).
  useEffect(() => {
    if (!enabled) return;

    const tryFetchStatus = async (url: string): Promise<{ ok: boolean; version: string | null }> => {
      try {
        const resp = await fetch(`${url}/api/status`, {
          signal: AbortSignal.timeout(5000),
        });
        if (!resp.ok) return { ok: false, version: null };
        try {
          const data = await resp.json();
          const ver = (data && typeof data.version === 'string') ? data.version : null;
          return { ok: true, version: ver };
        } catch {
          return { ok: true, version: null };
        }
      } catch {
        return { ok: false, version: null };
      }
    };

    const checkHealth = async () => {
      const primary = await tryFetchStatus(proxyUrl);
      if (primary.ok) {
        setLocalServiceConnected(true);
        setLocalServiceVersion(primary.version);
        setVersionCheckCompleted(true);
        console.log(`[TrendyolYemek] ✅ Local service bağlandı: ${proxyUrl} (version=${primary.version || 'unknown'})`);
        return;
      }

      // Configured URL failed - try localhost fallback if different
      if (proxyUrl !== 'http://localhost:3003') {
        const fallback = await tryFetchStatus('http://localhost:3003');
        if (fallback.ok) {
          setLocalServiceConnected(true);
          setLocalServiceVersion(fallback.version);
          setVersionCheckCompleted(true);
          console.log(`[TrendyolYemek] ✅ Local service localhost:3003 fallback (version=${fallback.version || 'unknown'})`);
          return;
        }
      }

      setLocalServiceConnected(false);
      setLocalServiceVersion(null);
      // Health check tamamlandı (başarısız ile): legacy mode'a düşmeye izin ver.
      setVersionCheckCompleted(true);
      console.warn('[TrendyolYemek] ❌ Local service erişilemez:', proxyUrl);
    };

    checkHealth();

    // Re-check every 30 seconds while disconnected
    const intervalId = setInterval(() => {
      checkHealth();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [enabled, proxyUrl]);

  // Polling sağlık izleme: local-service v1.7.8+ status endpoint'i `polling`
  // bloğu döner (lastTickAgeMs = son TAMAMLANAN tick'in yaşı, inFlightSinceMs =
  // askıdaki tick süresi). 2026-08-03 vakasında polling 11 saat kilitli kaldı ve
  // kimse fark etmedi; bu izleme, polling durduğunda ekranda uyarı çıkarmak için
  // `serverPollingStale` flag'ini besler. Eski servis sürümlerinde `polling`
  // alanı yoktur → stale asla true olmaz (yanlış alarm yok).
  useEffect(() => {
    if (!enabled || !supportsServerSidePolling) {
      setServerPollingStale(false);
      return;
    }

    const checkPollingHealth = async () => {
      try {
        const resp = await fetch(`${proxyUrl}/api/trendyol-polling/status`, {
          signal: AbortSignal.timeout(5000),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const polling = data?.polling;
        if (!polling) return; // eski sürüm - bilgi yok, alarm üretme
        const lastTickAgeMs = typeof polling.lastTickAgeMs === 'number' ? polling.lastTickAgeMs : null;
        const inFlightSinceMs = typeof polling.inFlightSinceMs === 'number' ? polling.inFlightSinceMs : null;
        // Tick her 10sn'de bir döner; 3dk'dan eski son tick veya 3dk'dır askıda
        // olan tick "polling durdu" demektir (watchdog 2dk'da toparlamalıydı).
        const stale = (lastTickAgeMs !== null && lastTickAgeMs > 180000)
          || (inFlightSinceMs !== null && inFlightSinceMs > 180000);
        setServerPollingStale(stale);
        if (stale) {
          console.warn('[TrendyolYemek] ⚠️ Local-service polling durmuş görünüyor:', polling);
        }
      } catch {
        // status okunamadı - bağlantı sorunu zaten localServiceConnected ile izleniyor
      }
    };

    checkPollingHealth();
    const intervalId = setInterval(checkPollingHealth, 60000);
    return () => clearInterval(intervalId);
  }, [enabled, supportsServerSidePolling, proxyUrl]);

  // PR3.2: server-side polling aktifse local-service'e credentials push et + 5dk heartbeat.
  // Sayfa unload'da sendBeacon ile config'i sil. Bu sayede local-service hangi şubelerin
  // hala aktif olduğunu bilir ve yalnızca onlar için polling yapar.
  useEffect(() => {
    if (!supportsServerSidePolling) return;
    if (!companyId) return;
    const credentials = getCredentials();
    if (!credentials) return;

    // BaseUrl - browser fetchOrders ile birebir aynı mantık (getBaseUrl).
    // STAGE veya PRODUCTION env'i lokaldeki TrendyolYemekService.ts'den okuyoruz.
    const trendyolEnv = (process.env.REACT_APP_TRENDYOL_ENVIRONMENT || 'STAGE').toUpperCase();
    const integrationUrl = trendyolEnv === 'PRODUCTION'
      ? 'https://api.tgoapis.com'
      : 'https://stageapi.tgoapis.com';
    const backendUrl = process.env.REACT_APP_TRENDYOLAPI_URL;
    const agentName = process.env.REACT_APP_TRENDYOL_AGENT_NAME || 'Aquasoft';

    if (!backendUrl) {
      console.warn('[TrendyolYemek] REACT_APP_TRENDYOLAPI_URL tanımsız - server-side polling backendUrl alamayacak');
    }

    // Trendyol API auth: apiKey:apiSecret Basic auth. Local-service hem "Basic XXX"
    // hem ham base64 token'ı kabul eder (header normalize ediyor).
    const basicTokenRaw = btoa(`${credentials.apiKey}:${credentials.apiSecret}`);

    let cancelled = false;
    const pushConfig = async () => {
      try {
        const resp = await fetch(`${proxyUrl}/api/trendyol-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId,
            branchId: branchId || null,
            storeId: credentials.storeId,
            supplierId: credentials.supplierId,
            token: `Basic ${basicTokenRaw}`,
            integrationUrl,
            vendor: 'trendyol_yemek',
            backendUrl: backendUrl || undefined,
            // Trendyol API'sinin zorunlu header'ları için ekstra bilgiler.
            // Local-service fetchTrendyolPackages bunları request header'larına ekler.
            userEmail: credentials.userEmail,
            agentName,
          }),
          signal: AbortSignal.timeout(5000),
        });
        if (resp.ok && !cancelled) {
          console.log('[TrendyolYemek] 🔧 Server-side polling config pushed');
        } else if (!resp.ok) {
          console.warn('[TrendyolYemek] Config push başarısız:', resp.status);
        }
      } catch (err) {
        if (!cancelled) console.warn('[TrendyolYemek] Config push hatası:', err);
      }
    };

    pushConfig();
    const heartbeat = setInterval(pushConfig, 5 * 60 * 1000); // 5dk

    const handleUnload = () => {
      try {
        const url = `${proxyUrl}/api/trendyol-config/delete?companyId=${encodeURIComponent(companyId)}${branchId ? `&branchId=${encodeURIComponent(branchId)}` : ''}`;
        const payload = JSON.stringify({ companyId, branchId: branchId || null });
        // sendBeacon DELETE'i desteklemediği için POST endpoint kullanıyoruz (LS'de tanımlı).
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } catch { /* unload sırasında her ihtimale karşı sessizce yut */ }
    };
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);

    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      // Component unmount'unda da config'i sil (SignalR push gereksiz devam etmesin).
      handleUnload();
    };
  }, [supportsServerSidePolling, companyId, branchId, getCredentials, proxyUrl]);

  // PR3.4: 1 dakikalık periyodik SQL safety-net.
  // - Eski mod (legacy polling): 25sn polling zaten var. Bu safety-net "kaçırılmış"
  //   siparişler için tampon recovery; idempotent merge yapar, prev state'i bozmaz.
  // - Yeni mod (server-side polling): SignalR push miss / disconnect sırasında
  //   sipariş kaçırmamak için kritik senkron noktası.
  useEffect(() => {
    if (!enabled || !companyId) return;

    const tick = async () => {
      try {
        const credentials = getCredentials();
        if (!credentials) return;
        const myStoreId = credentials.storeId ? Number(credentials.storeId) : null;
        const all = await getTodayOrdersFromSql(branchId, 1, 500, companyId);
        const filtered = myStoreId ? all.filter(o => Number(o.storeId) === myStoreId) : all;

        if (filtered.length === 0) return;

        setTrendyolOrders(prev => {
          const prevMap = new Map(prev.map(o => [o.id, o]));
          for (const sqlOrder of filtered) {
            const existing = prevMap.get(sqlOrder.id);
            if (!existing) {
              // Hiç yoktu — yeni sipariş, SQL'den ekle.
              prevMap.set(sqlOrder.id, sqlOrder);
            } else {
              // Vardı — optimistic gtsPaid bayrağını koru, SQL'in geri kalanını al.
              // (invoice/paid alanları TrendyolOrder DTO'sunda flat yer almıyor;
              // gtsPaid backend'in SQL kaydından türetiliyor — onu korumak yeterli.)
              prevMap.set(sqlOrder.id, {
                ...sqlOrder,
                gtsPaid: existing.gtsPaid || sqlOrder.gtsPaid,
              });
            }
          }
          return Array.from(prevMap.values());
        });
      } catch (err) {
        console.warn('[TrendyolYemek] 1dk safety-net SQL hatası:', err);
      }
    };

    const interval = setInterval(tick, 60 * 1000);
    return () => clearInterval(interval);
  }, [enabled, companyId, branchId, getCredentials]);

  const updateOrderStatus = useCallback(async (
    packageId: string,
    action: 'accept' | 'ready' | 'shipped' | 'delivered',
    preparationTime: number = 30,
  ): Promise<TrendyolUpdateResult> => {
    const credentials = getCredentials();
    if (!credentials) {
      const msg = 'Trendyol Yemek credential bilgileri bulunamadı';
      setError(msg);
      return { success: false, statusMismatch: false, message: msg };
    }

    try {
      switch (action) {
        case 'accept':
          await acceptOrder(credentials, packageId, preparationTime, proxyUrl);
          break;
        case 'ready':
          await markReady(credentials, packageId, proxyUrl);
          break;
        case 'shipped':
          await markShipped(credentials, packageId, proxyUrl);
          break;
        case 'delivered':
          await markDelivered(credentials, packageId, proxyUrl);
          break;
      }

      const statusMap: Record<string, string> = {
        accept: 'Picking',
        ready: 'Invoiced',
        shipped: 'Shipped',
        delivered: 'Delivered',
      };

      const currentOrder = trendyolOrders.find(o => o.id === packageId);

      setTrendyolOrders(prev => prev.map(o =>
        o.id === packageId ? { ...o, packageStatus: statusMap[action] } : o
      ));

      if (companyId) {
        updateStatusInSql(packageId, statusMap[action], undefined, currentOrder, branchId).catch(() => {});
      }

      return { success: true };
    } catch (err) {
      // 409 Conflict: Trendyol durum makinesi reddetti. Gerçek durumu çek, state'i senkronla.
      if (err instanceof TrendyolConflictError) {
        // Mevcut local state'teki sipariş (tanılama için)
        const localOrder = trendyolOrders.find(o => o.id === packageId);
        console.warn('[TrendyolYemek] 409 Conflict — durum Trendyol\'dan tekrar çekiliyor...', {
          packageId,
          action,
          rawBody: err.rawBody?.substring(0, 300),
          localPackageStatus: localOrder?.packageStatus,
          localStoreId: localOrder?.storeId,
          credentialsStoreId: credentials.storeId,
          credentialsSupplierId: credentials.supplierId,
        });
        try {
          const refreshed = await fetchOrders(credentials, undefined, proxyUrl);
          const realOrder = refreshed.find(o => o.id === packageId);

          // Tanılama: refresh listesinin içeriği + arama sonucu
          console.warn('[TrendyolYemek] Self-heal refresh sonucu:', {
            searchedPackageId: packageId,
            refreshedCount: refreshed.length,
            refreshedIds: refreshed.map(o => o.id),
            refreshedStatuses: refreshed.map(o => ({ id: o.id, status: o.packageStatus })),
            matched: !!realOrder,
            realStatus: realOrder?.packageStatus,
          });

          if (realOrder) {
            // EŞLEŞTİ: Trendyol'dan gerçek durum geldi → state ve SQL'i güvenle senkronla.
            const realStatus = realOrder.packageStatus;
            console.warn(`[TrendyolYemek] Self-heal: gerçek durum "${realStatus}", denenen aksiyon "${action}"`);

            // Tüm alanları (gtsPaid dahil) en güncel haliyle senkronla.
            setTrendyolOrders(prev => {
              const exists = prev.some(o => o.id === packageId);
              if (!exists) return [...prev, realOrder];
              return prev.map(o => (o.id === packageId ? { ...o, ...realOrder } : o));
            });

            if (companyId) {
              updateStatusInSql(packageId, realStatus, undefined, realOrder, branchId).catch(() => {});
            }

            return { success: false, statusMismatch: true, currentStatus: realStatus, attemptedAction: action };
          }

          // EŞLEŞMEDİ: Trendyol aktif liste sorgusunda yok.
          // Fallback: 409 hata gövdesinden gerçek durumu parse etmeyi dene.
          // Trendyol mesaj formatı: "X statüsünden Y statüsüne geçilemez" — X = gerçek durum.
          // Bu, default fetchOrders sorgusunda hariç tutulan Delivered/Returned gibi
          // statüleri otomatik yakalar.
          const parsedStatus = parseStatusFromConflictBody(err.rawBody);
          if (parsedStatus) {
            console.warn(`[TrendyolYemek] Self-heal: 409 mesajından gerçek durum okundu — "${parsedStatus}", denenen aksiyon "${action}"`);

            // State'i parsed status ile güncelle (diğer alanlar korunur).
            setTrendyolOrders(prev => prev.map(o =>
              o.id === packageId ? { ...o, packageStatus: parsedStatus } : o
            ));

            // SQL'e de yaz: localOrder'ı geçirerek diğer field'ların kaybolmasını önle.
            if (companyId) {
              updateStatusInSql(packageId, parsedStatus, undefined, localOrder, branchId).catch(() => {});
            }

            return { success: false, statusMismatch: true, currentStatus: parsedStatus, attemptedAction: action };
          }

          // Hâlâ bulamadık: ne listede var ne de mesajdan parse edilebilir.
          // Veriyi BOZMA: state'e dokunma, SQL'e yazma. Server-side polling 10sn'de bir tazeliyor.
          console.error('[TrendyolYemek] Self-heal: sipariş Trendyol aktif listesinde bulunamadı + 409 gövdesi parse edilemedi — state ve SQL korunuyor (yazılmıyor).', {
            searchedPackageId: packageId,
            localPackageStatus: localOrder?.packageStatus,
            attemptedAction: action,
            rawBodyPreview: err.rawBody?.substring(0, 200),
          });

          return { success: false, statusMismatch: true, currentStatus: 'not-listed', attemptedAction: action };
        } catch (refreshErr) {
          console.error('[TrendyolYemek] Self-heal sırasında refresh hatası:', refreshErr);
          return { success: false, statusMismatch: true, currentStatus: 'refresh-failed', attemptedAction: action };
        }
      }

      const errMsg = err instanceof Error ? err.message : 'Bilinmeyen hata';
      setError(errMsg);
      console.error(`[TrendyolYemek] ${action} hatası:`, errMsg);
      return { success: false, statusMismatch: false, message: errMsg };
    }
  }, [getCredentials, proxyUrl, companyId, branchId, trendyolOrders]);

  const cancelTrendyolOrder = useCallback(async (
    packageId: string,
    itemIdList: string[],
    reasonId: number,
  ): Promise<boolean> => {
    const credentials = getCredentials();
    if (!credentials) {
      setError('Trendyol Yemek credential bilgileri bulunamadı');
      return false;
    }

    try {
      await cancelOrder(credentials, packageId, itemIdList, reasonId, proxyUrl);

      const currentOrder = trendyolOrders.find(o => o.id === packageId);

      setTrendyolOrders(prev => prev.map(o =>
        o.id === packageId ? { ...o, packageStatus: 'Unsupplied' } : o
      ));

      if (companyId) {
        updateStatusInSql(packageId, 'Unsupplied', undefined, currentOrder, branchId).catch(() => {});
      }

      return true;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Bilinmeyen hata';
      setError(errMsg);
      console.error('[TrendyolYemek] İptal hatası:', errMsg);
      return false;
    }
  }, [getCredentials, proxyUrl, companyId, trendyolOrders]);

  const openVendor = useCallback(async (): Promise<boolean> => {
    const credentials = getCredentials();
    if (!credentials) return false;

    try {
      await updateStoreStatus(credentials, 'OPEN', proxyUrl);
      setVendorStatus('OPEN');
      return true;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Bilinmeyen hata';
      setError(errMsg);
      return false;
    }
  }, [getCredentials, proxyUrl]);

  const closeVendor = useCallback(async (): Promise<boolean> => {
    const credentials = getCredentials();
    if (!credentials) return false;

    try {
      await updateStoreStatus(credentials, 'CLOSED', proxyUrl);
      setVendorStatus('CLOSED');
      return true;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Bilinmeyen hata';
      setError(errMsg);
      return false;
    }
  }, [getCredentials, proxyUrl]);

  const refreshOrders = useCallback(async () => {
    await pollOrders();
  }, [pollOrders]);

  return {
    trendyolOrders,
    setTrendyolOrders,
    isPolling,
    vendorStatus,
    error,
    lastPollAt,
    localServiceConnected,
    localServiceVersion,
    supportsServerSidePolling,
    serverPollingStale,
    updateOrderStatus,
    cancelTrendyolOrder,
    openVendor,
    closeVendor,
    refreshOrders,
    trendyolProductDescriptions,
  };
}
